import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import type { EndUsePhotoGroup, EndUseRecord } from '../services/api';
import { uploadLiteratureNoteImage } from '../utils/uploadLiteratureNoteImage';
import { resolveUploadAssetUrl } from '../utils/resolveUploadAssetUrl';
import { buildEndUsePayload, photoGroupsFromRecord } from '../utils/endUseRecord';

interface EndUseRecordFormProps {
  photoGroups: EndUsePhotoGroup[];
  initialRecord?: EndUseRecord | null;
  onSubmit: (payload: ReturnType<typeof buildEndUsePayload>) => Promise<void>;
  submitLabel?: string;
  disabled?: boolean;
}

const EndUseRecordForm: React.FC<EndUseRecordFormProps> = ({
  photoGroups,
  initialRecord,
  onSubmit,
  submitLabel = '提交结束使用记录',
  disabled = false
}) => {
  const [notes, setNotes] = useState(initialRecord?.notes || '');
  const [photoUrls, setPhotoUrls] = useState<Record<string, string[]>>(() =>
    photoGroupsFromRecord(initialRecord || undefined, photoGroups)
  );
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setNotes(initialRecord?.notes || '');
    setPhotoUrls(photoGroupsFromRecord(initialRecord || undefined, photoGroups));
  }, [initialRecord, photoGroups]);

  const handleFiles = async (groupKey: string, files: FileList | null, maxPhotos: number) => {
    if (!files?.length) return;
    const group = photoGroups.find((g) => g.key === groupKey);
    if (!group) return;

    setError('');
    setUploadingKey(groupKey);
    try {
      const current = photoUrls[groupKey] || [];
      const remaining = Math.max(0, maxPhotos - current.length);
      const toUpload = Array.from(files).slice(0, remaining);
      if (toUpload.length < files.length) {
        setError(`「${group.label}」最多还可上传 ${remaining} 张`);
      }
      const newUrls: string[] = [];
      for (const file of toUpload) {
        const url = await uploadLiteratureNoteImage(file);
        newUrls.push(url);
      }
      setPhotoUrls((prev) => ({
        ...prev,
        [groupKey]: [...(prev[groupKey] || []), ...newUrls].slice(0, maxPhotos)
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploadingKey(null);
    }
  };

  const removePhoto = (groupKey: string, index: number) => {
    setPhotoUrls((prev) => {
      const list = [...(prev[groupKey] || [])];
      list.splice(index, 1);
      return { ...prev, [groupKey]: list };
    });
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const payload = buildEndUsePayload(notes, photoUrls, photoGroups);
      await onSubmit(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (!photoGroups.length) {
    return <p className="text-muted small mb-0">管理员尚未配置结束使用照片组。</p>;
  }

  return (
    <div>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      <Form.Group className="mb-3">
        <Form.Label>使用后状态说明</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="请描述使用后仪器整体状态、异常说明等…"
          disabled={disabled || submitting}
        />
      </Form.Group>

      {photoGroups.map((group) => {
        const max = group.maxPhotos ?? 3;
        const urls = photoUrls[group.key] || [];
        const isUploading = uploadingKey === group.key;
        return (
          <Form.Group key={group.key} className="mb-3">
            <Form.Label>
              {group.label}
              {group.required !== false && <span className="text-danger ms-1">*</span>}
              <span className="text-muted small ms-2">
                （最多 {max} 张，已 {urls.length} 张）
              </span>
            </Form.Label>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {urls.map((url, idx) => (
                <div key={`${group.key}-${idx}`} className="position-relative">
                  <img
                    src={resolveUploadAssetUrl(url) || url}
                    alt=""
                    style={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #dee2e6'
                    }}
                  />
                  {!disabled && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0"
                      style={{ padding: '0 4px', fontSize: 12, lineHeight: 1.2 }}
                      onClick={() => removePhoto(group.key, idx)}
                      aria-label="删除"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            {!disabled && urls.length < max && (
              <div>
                <Form.Control
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={isUploading || submitting}
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    void handleFiles(group.key, input.files, max);
                    input.value = '';
                  }}
                />
                {isUploading && (
                  <span className="small text-muted ms-2">
                    <Spinner size="sm" animation="border" /> 上传中…
                  </span>
                )}
              </div>
            )}
          </Form.Group>
        );
      })}

      {!disabled && (
        <Button variant="primary" disabled={submitting} onClick={() => void handleSubmit()}>
          {submitting ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              提交中…
            </>
          ) : (
            submitLabel
          )}
        </Button>
      )}
    </div>
  );
};

export default EndUseRecordForm;
